import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCancellationPolicy } from "../packageService";
import {
  pbAddBtn,
  pbDeleteBtn,
  pbEmptyState,
  pbIndexBadge,
  pbItemCardShell,
  pbSectionTitle,
} from "../packageBuilderUi";

const clampRefundPercentage = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  return Math.min(100, Math.max(0, num));
};

const Cancellation = ({ setCancellationData, packageid, onUserEdit }) => {
  const [rules, setRules] = useState([
    {
      package_id: packageid,
      is_available: true,
      days_type: "days",
      cancellation_days: "",
      refund_percentage: "",
    },
  ]);

  const { data: cancellationData, isLoading } = useQuery({
    queryKey: ["cancellation", packageid],
    queryFn: () => getCancellationPolicy(packageid),
    enabled: !!packageid,
    staleTime: 60_000,
  });

  // Update rules when data is loaded
  useEffect(() => {
    if (!packageid || isLoading) return;

    if (cancellationData?.data?.length) {
      const formattedRules = cancellationData.data.map((rule) => ({
        id: rule.id,
        package_id: rule.package_id,
        is_available: rule.is_available === 1,
        days_type: rule.days_type,
        cancellation_days: rule.cancellation_days,
        cancellation_days_from: rule.cancellation_days_from,
        cancellation_days_to: rule.cancellation_days_to,
        refund_percentage: clampRefundPercentage(rule.refund_percentage),
      }));

      setRules(formattedRules);
      setCancellationData({ 
        package_id: packageid, 
        data: formattedRules,
        isUpdate: cancellationData.data.length > 0,
        is_draft: 0
      });
    } else if (setCancellationData) {
      setCancellationData({
        package_id: packageid,
        data: rules.map((rule) => ({
          ...rule,
          is_available: rule.is_available ? 1 : 0,
        })),
        isUpdate: false,
        is_draft: 0,
      });
    }
  }, [cancellationData, packageid, setCancellationData, isLoading]);

  const handleAddRule = () => {
    onUserEdit?.();
    const newRule = {
      is_available: true,
      days_type: "days",
      cancellation_days: "",
      refund_percentage: "",
    };
    const newRules = [...rules, newRule];
    setRules(newRules);
    
    // Update parent with new rules
    setCancellationData({
      package_id: packageid,
      data: newRules.map(rule => ({
        ...rule,
        is_available: rule.is_available ? 1 : 0,
      })),
      isUpdate: cancellationData?.data?.length > 0,
      is_draft: 0
    });
  };

  const handleRemoveRule = (index) => {
    onUserEdit?.();
    const updatedRules = rules.filter((_, i) => i !== index);
    setRules(updatedRules);

    // Update parent with remaining rules
    setCancellationData({
      package_id: packageid,
      data: updatedRules.map(rule => ({
        ...rule,
        is_available: rule.is_available ? 1 : 0,
      })),
      isUpdate: cancellationData?.data?.length > 0,
      is_draft: 0
    });
  };

  const handleRuleChange = (index, field, value) => {
    onUserEdit?.();
    const nextValue =
      field === "refund_percentage" ? clampRefundPercentage(value) : value;
    const newRules = [...rules];
    newRules[index] = {
      ...newRules[index],
      [field]: nextValue,
    };
    setRules(newRules);

    // Update parent component with the latest rules
    const formattedData = {
      package_id: packageid,
      data: newRules.map(rule => ({
        ...rule,
        is_available: rule.is_available ? 1 : 0,
        refund_percentage: clampRefundPercentage(rule.refund_percentage),
      })),
      isUpdate: cancellationData?.data?.length > 0,
      is_draft: 0
    };
    setCancellationData(formattedData);
  };

  return (
    <div className="p-0">
      {isLoading ? (
        <div className={pbEmptyState}>
          Loading cancellation policy...
        </div>
      ) : (
        <>
      <p className="mb-4 text-sm text-gray-500">
        Define your package cancellation rules and refund percentages.
      </p>

      <div className="space-y-4">
        {rules.map((rule, index) => (
          <div key={index} className={pbItemCardShell}>
            <div className="border-b border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className={pbIndexBadge}>{index + 1}</div>
                  <h3 className={pbSectionTitle}>
                    Cancellation rule {index + 1}
                  </h3>
                </div>
                <button
                  onClick={() => handleRemoveRule(index)}
                  className={pbDeleteBtn}
                  title="Remove rule"
                >
                  <i className="fi fi-rr-trash text-xs"></i>
                </button>
              </div>
            </div>

            <div className="space-y-4 p-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {/* Days Type Selection */}
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">
                    Days/Days Range <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={rule.days_type}
                    onChange={(e) =>
                      handleRuleChange(index, "days_type", e.target.value)
                    }
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-800 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                  >
                    <option value="days">Days</option>
                    <option value="days_range">Days Range</option>
                  </select>
                </div>

                {/* Conditional rendering based on days_type */}
                {rule.days_type === "days" ? (
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                      Before <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={rule.cancellation_days}
                      onChange={(e) =>
                        handleRuleChange(
                          index,
                          "cancellation_days",
                          e.target.value
                        )
                      }
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-800 placeholder:text-sm placeholder:font-normal placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                      placeholder="Enter days"
                    />
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-gray-700">
                        From <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={rule.cancellation_days_from}
                        onChange={(e) =>
                          handleRuleChange(
                            index,
                            "cancellation_days_from",
                            e.target.value
                          )
                        }
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-800 placeholder:text-sm placeholder:font-normal placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                        placeholder="Enter days"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-gray-700">
                        To <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={rule.cancellation_days_to}
                        onChange={(e) =>
                          handleRuleChange(
                            index,
                            "cancellation_days_to",
                            e.target.value
                          )
                        }
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-800 placeholder:text-sm placeholder:font-normal placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                        placeholder="Enter days"
                      />
                    </div>
                  </>
                )}

                {/* Refund Percentage */}
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">
                    Refund Percentage <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={rule.refund_percentage}
                      onChange={(e) =>
                        handleRuleChange(
                          index,
                          "refund_percentage",
                          e.target.value
                        )
                      }
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-800 placeholder:text-sm placeholder:font-normal placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                      placeholder="Enter percentage"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <span className="text-gray-500">%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={handleAddRule}
          className={pbAddBtn}
        >
          Add rule
          <i className="fi fi-rr-plus text-xs"></i>
        </button>

        <div className="rounded-xl border border-amber-100 bg-amber-50/80 p-4">
          <div className="flex gap-3">
            <i className="fi fi-rr-info relative top-0.5 text-amber-500"></i>
            <div>
              <h3 className="text-sm font-bold text-amber-900">
                Important note
              </h3>
              <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-amber-800">
                  <li>All fields marked with * are required</li>
                  <li>Refund percentage must be between 0 and 100</li>
                  <li>Days must be positive numbers</li>
                  <li>For date ranges, "From" date must be less than "To" date</li>
                </ul>
            </div>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default Cancellation;
